<template>
	<v-defaults-provider :defaults="overlayDefaults">
		<v-card id="library" class="fill-height">
			<Toolbar :items="toolbarItems" :panelParams="params"/>
			<Toolbar :items="headerItems"/>

			<perfect-scrollbar class="scroll-max-height" v-if="assets.loaded">
				<v-list v-if="assetsData.length">
					<draggable
						:list="assetsData"
						:group="{ name: 'library-assets', pull: 'clone', put: false }"
						:clone="handleDraggedClone"
						:move="onMove"
						@end="onDropAsset"
						:force-fallback="true"
						:fallback-on-body="true"
						filter=".not-draggable"
						drag-class="dragged-library"
						data-drop-container="library"
						class="drop-container">
						<LibraryItem v-for="asset in assetsData"
							:key="asset.AssetID"
							:selectedStationID="selectedStationID"
							:asset="asset"
							:class="{'not-draggable': !isDraggableAsset(asset)}"
						/>
					</draggable>
				</v-list>
				<v-container v-if="!filters.assetTypeID">
					<div class="text-caption text-center">
						Select a filter and type a Title or an Artist in the Search Library field.
					</div>
				</v-container>
				<v-container v-else-if="!assets.data.length">
					<div class="text-caption text-center">No assets found.</div>
				</v-container>
			</perfect-scrollbar>
			<Loader :value="!assets.loaded"/>
		</v-card>
	</v-defaults-provider>
</template>

<script>
import config from "@/config";

/* Libraries */
import {VueDraggableNext} from "vue-draggable-next";
import moment from "moment";
import _ from "lodash";

/* Modules */
import {VAvatar, VCombobox, VListItem, VSelect} from "vuetify/components";
import Image from "@/components/Image.vue";
import LibraryItem from "@/components/LibraryItem.vue";
import Loader from "@/components/Loader.vue";
import Toolbar from "@/components/Toolbar.vue";

/* Mixins */
import DragAndDropMixin from "@/mixins/DragAndDrop";
import PanelPropertiesMixin from "@/mixins/PanelProperties";

export default {
	name: "Library",

	components: {
		draggable: VueDraggableNext,
		Toolbar,
		LibraryItem,
		Loader
	},

	mixins: [DragAndDropMixin, PanelPropertiesMixin],

	props: ["params"],

	data() {
		return {
			search: {},
			filters: {
				stationID: null,
				assetTypeID: null,
				limit: 100,
				skip: 0,
				sortField: "Title",
				sortOrder: "ASC",
				searchField: "",
				filter: ""
			},
			draggedEvent: null,
			dropTargetEvent: null,
			dropContainer: null,
			componentName: "library"
		}
	},

	computed: {
		toolbarItems() {
			return [
				//todo: re-enable when we implement each feature
				/*{
					component: VTooltip,
					props: {
						text: "Add Asset",
						location: "bottom",
						openDelay: 500
					},
					slots: [
						{
							name: "activator",
							items: (data) => [
								{
									component: VBtn,
									props: {
										...data.props,
										icon: "mdi-plus-box",
										// icon: "mdi-plus-box-multiple",
										color: "primary",
										rounded: false,
										size: 40
									}
								}
							]
						}
					]
				},
				{
					component: VTooltip,
					props: {
						text: "Remove Asset",
						location: "bottom",
						openDelay: 500
					},
					slots: [
						{
							name: "activator",
							items: (data) => [
								{
									component: VBtn,
									props: {
										...data.props,
										icon: "mdi-minus-box",
										// icon: "mdi-minus-box-multiple",
										color: "error",
										rounded: false,
										size: 40
									}
								}
							]
						}
					]
				},
				{
					component: VTooltip,
					props: {
						text: "Mass Import",
						location: "bottom",
						openDelay: 500
					},
					slots: [
						{
							name: "activator",
							items: (data) => [
								{
									component: VBtn,
									props: {
										...data.props,
										icon: "mdi-cloud-upload",
										rounded: false,
										size: 40
									}
								}
							]
						}
					]
				},
				{
					component: VTooltip,
					props: {
						text: "Mass Changer",
						location: "bottom",
						openDelay: 500
					},
					slots: [
						{
							name: "activator",
							items: (data) => [
								{
									component: VBtn,
									props: {
										...data.props,
										icon: "mdi-layers-triple",
										// icon: "mdi-pencil-box-multiple",
										rounded: false,
										size: 40
									}
								}
							]
						}
					]
				}*/
			];
		},

		headerItems() {
			return [
				{
					...(!this.globalSearch && {
						component: VSelect,
						props: {
							modelValue: this.filters.sortField,
							items: this.sortByData,
							class: "mr-2",
							label: "Sort by",
							variant: "outlined",
							density: "compact",
							hideDetails: true,
							menuProps: {"max-height": 250},
							width: 120
						},
						actions: {
							"update:model-value": (data) => {
								this.filters.sortField = data;
								if (this.filters.assetTypeID) {
									this.searchAssets();
								}
							}
						}
					})
				},
				{
					component: VSelect,
					props: {
						modelValue: this.filters.assetTypeID,
						items: this.assetTypes,
						itemTitle: "Description",
						itemValue: "AssetTypeID",
						class: "mr-2",
						label: "Filter",
						variant: "outlined",
						density: "compact",
						hideDetails: true,
						menuProps: {"max-height": 250},
						clearable: true,
						width: 120
					},
					actions: {
						"update:model-value": (assetTypeID) => {
							this.$store.commit("resetLibraryAssets");
							this.filters.assetTypeID = assetTypeID;
							if (assetTypeID) {
								this.$store.dispatch("updateAssetsLastAccessed", assetTypeID);
							}
							this.searchAssets();
						}
					}
				},
				{
					...(this.selectedStation && {
						component: VSelect,
						props: {
							modelValue: this.filters.stationID,
							items: [
								{
									image: `${config.PORTAL_URL}/content/stations/${this.selectedStationID}/image`,
									title: this.selectedStation?.StationName ?? null,
									value: this.selectedStationID,
									type: "text"
								},
								{
									image: config.ORIGIN === "SOUNDPLUS" ? "soundplus-logo.png" :
									       `${config.PORTAL_URL}/content/accounts/${this.$store.getters.getOrgID()}/image`,
									title: "All Stations",
									value: ""
								}
							],
							class: "search-select",
							variant: "outlined",
							density: "compact",
							hideDetails: true,
							menuProps: {maxHeight: 250, width: 55},
							width: 55,
							height: 40
						},
						slots: [
							{
								name: "selection",
								items: (data) => [
									{
										component: VAvatar,
										props: {
											size: 24
										},
										slots: [
											{
												name: "default",
												items: [
													{
														component: Image,
														props: {
															img: data.item.raw.image,
															width: 24,
															height: 24,
															eager: true
														}
													}
												]
											}
										]
									}
								]
							},
							{
								name: "item",
								items: (data) => [
									{
										component: VListItem,
										props: {...data.props, density: "compact"},
										slots: [
											{
												name: "title",
												items: [
													{
														component: VAvatar,
														props: {
															size: 24
														},
														slots: [
															{
																name: "default",
																items: [
																	{
																		component: Image,
																		props: {
																			img: data.item.raw.image,
																			width: 24,
																			height: 24,
																			eager: true
																		}
																	}
																]
															}
														]
													}
												]
											}
										]
									}
								]
							}
						],
						actions: {
							"update:model-value": (objectID) => {
								this.filters.stationID = objectID;
								this.searchAssets();
							}
						}
					})
				},
				{
					component: VCombobox,
					props: {
						modelValue: this.filters.searchField,
						items: this.recentlySearched,
						class: "search-input",
						prependInnerIcon: "mdi-magnify",
						variant: "outlined",
						density: "compact",
						flat: true,
						clearable: true,
						singleLine: true,
						hideDetails: true,
						placeholder: "Search Library",
						width: 160
					},
					actions: {
						"update:modelValue": (value) => {
							if (this.timeout) {
								this.$worker.clearTimeout(this.timeout);
								this.timeout = null;
							}

							this.timeout = this.$worker.setTimeout(() => {
								if (value) {
									this.search["OR"] = [];
									this.search["OR"].push({Title: {REGEX: value, OPTIONS: "i"}});
									this.search["OR"].push({Artist: {REGEX: value, OPTIONS: "i"}});
									this.search["OR"].push({
										ThirdPartyIDs: {
											MusicPointID: {
												REGEX: value, OPTIONS: "i"
											}
										}
									});
									this.search["OR"].push({ThirdPartyIDs: {ExternalID: {REGEX: value, OPTIONS: "i"}}});
									this.$store.commit("setRecentlySearched", value);
									this.$store.commit(
										"setUserPreferences",
										{librarySearchedTerms: this.recentlySearched}
									);
								}
								else {
									delete this.search["OR"];
								}
								this.filters.searchField = value;
								this.searchAssets();
							}, 750);
						}
					}
				}
			]
		},

		assets() {
			return this.$store.getters.getLibraryAssets();
		},

		assetsData() {
			return this.assets.data.filter(a => !a?.isDeleted);
		},

		globalSearch() {
			return !this.filters.stationID;
		},

		recentlySearched() {
			return this.$store.getters.getRecentlySearched();
		},

		sortByData() {
			return [
				{
					title: "Title",
					value: "Title"
				},
				{
					title: "Artist",
					value: "Artist"
				},
				{
					title: "Length",
					value: "POIMarkup.Length"
				}
			]
		},

		assetTypes() {
			const assetTypes = this.$store.getters.getAssetTypes();
			if (this.globalSearch) {
				return [{Description: "Global", AssetTypeID: null}, ...assetTypes];
			}
			else {
				return assetTypes;
			}
		}
	},

	methods: {
		searchAssets() {
			if (this.filters.assetTypeID || (this.globalSearch && !_.isEmpty(this.filters.searchField))) {
				this.filters.filter = JSON.stringify(this.search);
				this.$store.dispatch("getLibraryAssets", this.filters).then(() => {
					let libraryAssets = this.$store.getters.userPreferences("libraryAssets");
					if (!this.filters.searchField && libraryAssets &&
					    libraryAssets.assetType === this.filters.assetTypeID) {
						this.$store.dispatch(
							"sortLibraryAssets",
							{assetType: libraryAssets.assetType, assetId: libraryAssets.assets}
						);
					}
				});
			}
		},

		isDraggableAsset(asset) {
			if (asset?.KillDateTime) {
				return moment().isBefore(asset?.KillDateTime);
			}
			return true;
		},

		onMove(evt, originalEvent) {
			const draggedElement = document.querySelector(".dragged-library");
			this.onDragItem(evt, originalEvent, draggedElement);
			return false;
		},

		onDropAsset(ev) {
			this.onDropItem(ev, true);

			const targetContainer = this.getContainer(ev.originalEvent);
			if (targetContainer) {
				this.$store.dispatch(
					"sortLibraryAssets",
					{assetType: this.draggedElementData.Asset.AssetTypeID, assetId: this.draggedElementData.AssetID}
				);
			}
		}
	},

	watch: {
		selectedStationID: {
			handler(stationID) {
				if (stationID) {
					this.filters.stationID = stationID;
				}
			},
			immediate: true
		}
	},

	created() {
		document.addEventListener("keydown", this.cancelDraggingOrCutOrCopyOnEscape);
	},

	beforeUnmount() {
		document.removeEventListener("keydown", this.cancelDraggingOrCutOrCopyOnEscape);
	},
}
</script>

<style lang="scss" scoped>
.fill-height {
	-webkit-user-select: none; /* Safari */
	-ms-user-select: none; /* IE 10 and IE 11 */
	user-select: none; /* Standard syntax */
}

.scroll-max-height {
	max-height: calc(100% - 102px)
}

:deep(.v-input) {
	&.search-select {
		.v-field {
			border-bottom-right-radius: 0;
			border-top-right-radius: 0;
		}
	}

	&.search-input {
		.v-field {
			border-bottom-left-radius: 0;
			border-top-left-radius: 0;
		}
	}
}
</style>